// styles.js
import styled from 'styled-components';

/* ---------- Layout ---------- */

export const Page = styled.div`
  min-height: 100vh;
  background: #ffffff;
`;

export const Container = styled.div`
  max-width: 48rem; /* ~768px */
  margin: 0 auto;
  padding: 1rem;

  @media (min-width: 640px) {
    padding: 1.5rem;
  }
`;

/* ---------- Buttons ---------- */

export const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
  padding: 0.5rem 1.5rem;
  border-radius: 9999px;
  font-weight: 500;
  color: #1f2937; /* gray-800 */
  background: #d2fa52;
  border: 0;
  cursor: pointer;

  &:hover {
    filter: brightness(0.98);
  }
`;

/* ---------- Headings / Text ---------- */

export const Title = styled.h1`
  text-align: center;
  margin: 0 0 1rem 0;
  color: #231f20; /* gray-900 */
  font-weight: 600;
  font-size: 32px;

  @media (min-width: 640px) {
    font-size: 1.875rem;
  }
  @media (min-width: 768px) {
    font-size: 2.25rem;
  }
`;

export const Subtitle = styled.p`
  text-align: center;
  color: #231f20; /* gray-600 */
  font-size: 1rem;

  @media (min-width: 640px) {
    font-size: 1.125rem;
  }
`;

export const HelpText = styled.p`
  margin-top: 0.5rem;
  font-size: 0.875rem;
  color: #6e6e6e; /* gray-500 */
`;

export const SmallErrorRow = styled.div`
  margin-top: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #ef4444; /* red-500 */
  font-size: 0.875rem;
`;

/* ---------- Cards ---------- */

export const DCard = styled.div`
  padding: 16px;
  background: #ffffff;
  border: 1px solid #005d1f;
  box-shadow: 0px 10px 20px rgba(0, 0, 0, 0.08);
  border-radius: 8px;
  margin-bottom: 32px;
`;

export const DCardHeader = styled.div`
  padding-bottom: 1.5rem;
`;

export const DCardTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  color: #111827; /* gray-900 */
  font-size: 18px;
  font-weight: 600;
`;

export const DCardContent = styled.div`
  padding: 0;

  @media (min-width: 640px) {
    padding: 0;
  }
`;

export const IconCircle = styled.div`
  width: 4rem;
  height: 4rem;
  border-radius: 1rem;
  background: #ecfdf5; /* green-100-ish */
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

/* ---------- Exception Banner ---------- */

const exceptionTokens = {
  warn: {
    bg: '#fff7ed', // orange-50
    border: '#fdba74', // orange-300
    text: '#9a3412', // orange-800
  },
  error: {
    bg: '#fef2f2', // red-50
    border: '#fca5a5', // red-300
    text: '#7f1d1d', // red-800
  },
};

export const ExceptionBanner = styled.div`
  margin-bottom: 1.5rem;
  border-radius: 0.75rem;
  border: 2px solid ${({ $tone }) => exceptionTokens[$tone || 'warn'].border};
  background: ${({ $tone }) => exceptionTokens[$tone || 'warn'].bg};
  color: ${({ $tone }) => exceptionTokens[$tone || 'warn'].text};
`;

export const ExceptionBody = styled.div`
  padding: 1.5rem;
  display: flex;
  gap: 0.75rem;
  align-items: flex-start;

  h3 {
    margin: 0 0 0.5rem 0;
    font-weight: 700;
    font-size: 1.125rem;
  }

  p {
    margin: 0;
  }
`;

/* ---------- Form ---------- */

export const FieldLabel = styled.label`
  display: block;
  margin-bottom: 8px;
  color: #111827;
  font-weight: 600;
  font-size: 16px;
`;

export const DateFieldWrap = styled.div`
  width: 100%;
  min-height: 48px;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: #ffffff;
  border: 1px solid #231f20; /* green-500 */
  border-radius: 8px;
  padding: 12px 16px;

  &:focus-within {
    border-color: #008427; /* green-600 */
  }
`;

export const Slash = styled.span`
  color: #9ca3af; /* gray-400 */
  user-select: none;
  font-weight: 500;
`;

export const DateInput = styled.input`
  width: ${p => p.$w || '2rem'};
  text-align: center;
  border: none;
  outline: none;
  background: transparent;
  font-size: 0.875rem;
  font-weight: 500;
  color: ${p => (p.$filled ? '#231F20' : '#6E6E6E')};
`;

export const YearInput = styled(DateInput)`
  width: 3rem;
`;

export const HiddenDateInput = styled.input`
  position: absolute;
  right: 0;
  top: 0;
  width: 1.5rem;
  height: 1.5rem;
  opacity: 0;
  cursor: pointer;
  z-index: 20;
`;

export const TextInput = styled.input`
  width: 100%;
  margin-top: 0;
  border-radius: 8px;
  border: 1px solid ${({ $error }) => ($error ? '#D60000' : '#231F20')};
  padding: 12px 16px;
  outline: none;
  color: #231f20;

  &:focus {
    border-color: #008427;
    box-shadow: none;
  }

  &::placeholder {
    color: #6e6e6e;
  }
`;

/* ---------- Captcha ---------- */

export const CaptchaBox = styled.div`
  background: #f1f5f7; /* gray-50 */
  padding: 2rem;
  border-radius: 0.5rem;
  border: 1px solid #f1f5f7; /* gray-200 */
  min-height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 24px;
`;

export const CaptchaInner = styled.div`
  text-align: center;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  .tile {
    display: inline-block;
    padding: 1rem;
    border: 1px solid #d1d5db;
    background: #ffffff;
    border-radius: 0.5rem;
    margin-bottom: 1rem;
  }

  .badge {
    margin-top: 0.25rem;
    font-size: 0.75rem;
    color: #6b7280;
  }

  p {
    font-size: 0.875rem;
    color: #4b5563;
    margin: 0;
  }
`;

/* ---------- Agreements ---------- */

export const AgreementRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
`;

export const ALabel = styled.label`
  font-size: 16px;
  color: #231f20;
  line-height: 1.25rem;
  font-weight: 400;
`;

export const ALink = styled.a`
  color: #005d1f;
  font-weight: 500;
  text-decoration: underline;
  display: inline-block;

  &:hover {
    text-decoration: underline;
  }
`;

export const ACheckBox = styled.label`
  position: relative;
  display: inline-block;
  width: 24px;
  height: 24px;
  border: none;
  border-radius: 4px;
  background: #e8eef1;
  cursor: pointer;
  flex-shrink: 0;
  border: 2px solid #e8eef1;
  transition: border-color 0.2s;

  &.disabled {
    background: #f3f4f6;
    cursor: not-allowed;
    opacity: 0.5;
  }

  &:hover,
  &:focus-within {
    border-color: #89d49f;
  }

  input {
    position: absolute;
    width: 100%;
    height: 100%;
    opacity: 0;
    cursor: pointer;
    margin: 0;
    z-index: 10;
  }

  svg {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    display: ${p => (p.$checked ? 'block' : 'none')};
  }
`;

/* ---------- Spacing helpers ---------- */

export const Section = styled.div`
  margin-bottom: 2rem;

  @media (min-width: 640px) {
    margin-bottom: 3rem;
  }
`;
